// src/data/documentVersion.js
//
// VERSION MARKING — the code and the timestamp that go on every document, so a reader can tell in
// one glance which one is live and which one is history.
//
// Bob, 2026-08-29: *"Everything gets marked with either a DV (only one of these - more creates
// confusion), however, the next steps should say something like V1, V2, V3, etc, or OV, FV with
// timestamp."*
//
// -------------------------------------------------------------------------------------------
// WHAT THIS MODULE DOES AND DOES NOT DO
//
// IT DOES     assign the code, keep the numbering monotonic, refuse a duplicate, build the
//             filename suffix and the watermark line, and answer "which one is live".
// IT DOES NOT render a watermark onto a PDF. That is a Cloud Function and a Storage change, and
//             it is ISS-46. A mark applied only in the browser is DECORATIVE - see ISS-46 for why:
//             `getDownloadURL()` writes a durable token-bearing URL onto the claim, and
//             storage.rules line 102 reads `allow read: if isAuth()`, so the unmarked original is
//             one click away from anyone signed in. This module is the vocabulary; the enforcement
//             is separate and is not pretended at here.
//
// AND IT NEVER MARKS THE ORIGINAL FILE. Bob's ruling of 2026-08-24: documents are handled as
// copies "so digital copies are never marred or defaced". The archive keeps the pristine
// instrument; the DERIVED copy carries the mark. Everything below describes the derived copy.
// -------------------------------------------------------------------------------------------

// THE CODES. The letters are Bob's; the expansions are the assistant's reading and are MARKED AS
// SUCH because they will appear on a page a carrier reads. Nothing here is invented into a legal
// document without his confirmation (PD-4, ruling 28) - see CODE_LABELS below.
export const CODES = {
  OV: 'OV',   // the original, as first produced. Exactly one, ever, and it never changes.
  V:  'V',    // V1, V2, V3 ... successive frozen versions between the original and the final.
  DV: 'DV',   // the ONE live editable copy. "only one of these - more creates confusion."
  FV: 'FV',   // the final version. At most one; once set the lineage is closed.
};

// *** THESE EXPANSIONS ARE UNCONFIRMED AND ARE FLAGGED IN THE UI AS SUCH. ***
// Bob gave the letters, not the words. They are here in ONE place so correcting them is a one-line
// edit and so no other file invents its own. Until he confirms, `LABELS_CONFIRMED` stays false and
// the watermark prints the CODE ALONE - a bare "DV" is honest, a wrong expansion on a document in
// front of a Board is not.
// Ruling 85 (2026-08-31): Bob confirmed OV / V / DV (Draft Version) / FV; "LABELS_CONFIRMED may be set true".
// Set 2026-10-06, Record 576 (mandate audit item 12).
export const LABELS_CONFIRMED = true;
export const CODE_LABELS = {
  OV: 'Original Version',
  V:  'Version',
  DV: 'Draft Version',
  FV: 'Final Version',
};

// DV IS THE SPINE, AND IT IS RULING 61.2 IN A FILENAME.
// "ONE claim. ONE editable copy. Whoever holds it is the only person who can type." Exactly one DV
// exists in a lineage at any moment. Bob's reason and 61.2's are the same one: more than one live
// copy is how two people edit two documents and both believe they hold the claim.
export const LIVE_CODE = CODES.DV;

// ---------------------------------------------------------------------------------------------
// THE TIMESTAMP. Two forms, on purpose, and they are not interchangeable.
//
//   SORT KEY   20260829T154200Z - UTC, compact, no punctuation a filesystem argues with, and it
//              sorts lexically in the same order as it sorts chronologically. This goes in the
//              FILENAME.
//   READABLE   29 Aug 2026, 11:42 EDT - what a human, a General Chairperson or a Board reads. This
//              goes in the WATERMARK.
//
// The zone is carried in both. A bare local time on a document that may be read in another zone
// years later is an ambiguity nobody can resolve afterwards.
// ---------------------------------------------------------------------------------------------
export function sortStamp(d) {
  const t = d instanceof Date ? d : new Date(d || Date.now());
  if (isNaN(t.getTime())) return null;
  return t.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

export function readableStamp(d, timeZone) {
  const t = d instanceof Date ? d : new Date(d || Date.now());
  if (isNaN(t.getTime())) return null;
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: timeZone || 'UTC', timeZoneName: 'short',
    });
    return fmt.format(t).replace(',', '');
  } catch (e) {
    return t.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  }
}

// The code for a version at position `n` in the lineage.
//   n = 0        the original            -> OV
//   0 < n        a superseded version    -> V1, V2, V3 ...
// `live` and `final` are roles that override the sequence code, because a document is the live one
// or the final one regardless of where it sits in the count.
export function codeFor(n, role) {
  if (role === 'live') return CODES.DV;
  if (role === 'final') return CODES.FV;
  const i = Number(n);
  if (!isFinite(i) || i < 0) return null;
  if (i === 0) return CODES.OV;
  return CODES.V + i;
}

// ---------------------------------------------------------------------------------------------
// THE NUMBERING, AND THE TRAP IT EXISTS TO AVOID.
//
// THIS MORNING, 2026-08-29, the quarantined scaffold at C:\ycp-platform numbered its records by
// COUNTING FILES:  $nextIndex = $existingClaims.Count + 1.  Delete one of three and the next
// record generated is number 3, which already exists, and Set-Content overwrote it silently. That
// is recorded at data\YCP_SECOND_REPO_AUDIT_2026-08-29.md SS3a.
//
// VERSION NUMBERING HAS THE IDENTICAL TRAP AND A WORSE CONSEQUENCE: two documents both marked V2,
// with different contents, both timestamped, and no way afterwards to say which one a carrier was
// served. So the next number is MAX + 1, never COUNT + 1, and a number already used is refused
// rather than reissued. Numbers are never reused even when a version is deleted.
// ---------------------------------------------------------------------------------------------
export function nextSequence(existing) {
  const used = (Array.isArray(existing) ? existing : [])
    .map(v => (v && typeof v === 'object') ? Number(v.sequence) : Number(v))
    .filter(n => isFinite(n) && n >= 0);
  if (!used.length) return 0;                 // an empty lineage starts at the original
  return Math.max.apply(null, used) + 1;
}

// Everything a derived copy needs to be marked, or an explanation of why it cannot be.
// Returns { ok, errors[], code, sequence, suffix, watermark } - never a partial mark.
export function markFor(input) {
  const i = input || {};
  const errors = [];

  const existing = Array.isArray(i.existing) ? i.existing : [];
  const role = i.role || null;                 // 'live' | 'final' | null

  if (role && role !== 'live' && role !== 'final') {
    errors.push('"' + role + '" is not a role. The roles are live (DV) and final (FV); everything '
              + 'else is positional - OV for the original and V1, V2, V3 for what follows.');
  }

  // "only one of these - more creates confusion". Enforced, not asked for.
  if (role === 'live' && existing.some(v => v && v.code === CODES.DV)) {
    errors.push('A DV already exists in this lineage. There is exactly one live editable copy, '
              + 'and a second one is how two officers edit two documents and both '
              + 'believe they hold the claim.');
  }
  if (role === 'final' && existing.some(v => v && v.code === CODES.FV)) {
    errors.push('An FV already exists. The final version closes the lineage; there is not a second final.');
  }

  const seq = (typeof i.sequence === 'number') ? i.sequence : nextSequence(existing);
  if (existing.some(v => v && Number(v.sequence) === seq)) {
    errors.push('Sequence ' + seq + ' is already used in this lineage. Numbers are never reused - '
              + 'two documents carrying the same mark with different contents cannot be told apart '
              + 'afterwards, which is the defect that matters.');
  }

  const code = codeFor(seq, role);
  if (!code) errors.push('No code could be assigned for sequence ' + JSON.stringify(i.sequence) + '.');

  const when = i.at ? new Date(i.at) : new Date();
  const sort = sortStamp(when);
  const human = readableStamp(when, i.timeZone);
  if (!sort || !human) errors.push('An unreadable timestamp cannot be marked onto a document.');

  if (errors.length) return { ok: false, errors, code: null, sequence: null, suffix: null, watermark: null };

  // THE FILENAME SUFFIX - "the file itself, not the actual document" (Bob, 2026-08-29).
  const suffix = '__' + code + '_' + sort;

  // THE WATERMARK LINE. While LABELS_CONFIRMED is false this prints the CODE ALONE. A bare "DV" is
  // honest; a guessed expansion on a page a Board reads is the kind of invention ruling 28 exists
  // to stop. The reason clause is appended only when one was given - never fabricated.
  const name = LABELS_CONFIRMED ? (CODE_LABELS[code.replace(/\d+$/, '')] || code) : code;
  const label = LABELS_CONFIRMED && /^V\d+$/.test(code) ? (CODE_LABELS.V + ' ' + code.slice(1)) : name;
  const reason = String(i.reason || '').trim();
  const watermark = [label, human].join('  -  ') + (reason ? '  -  ' + reason : '');

  return { ok: true, errors: [], code, sequence: seq, suffix, watermark };
}

// Which entry in a lineage is the one anybody should be working from.
// Returns null rather than guessing when there is no live copy - a lineage with no DV is a defect
// to report, not a prompt to pick the newest and hope.
export function liveVersion(lineage) {
  const list = Array.isArray(lineage) ? lineage : [];
  const live = list.filter(v => v && v.code === CODES.DV);
  return live.length === 1 ? live[0] : null;
}

export function isSuperseded(entry) {
  const c = entry && entry.code;
  return !!c && c !== CODES.DV;
}

// Applies the suffix to a filename WITHOUT disturbing its extension, so `Barber_Separation.pdf`
// becomes `Barber_Separation__V2_20260829T154200Z.pdf` and stays a PDF to every reader.
export function suffixFilename(filename, suffix) {
  const f = String(filename || '').trim();
  if (!f) return null;
  if (!suffix) return f;
  const dot = f.lastIndexOf('.');
  if (dot <= 0) return f + suffix;
  return f.slice(0, dot) + suffix + f.slice(dot);
}

// ---------------------------------------------------------------------------------------------
// LINEAGES - added 2026-08-29 when this module was finally wired to a page.
//
// A LINEAGE IS A DOCUMENT THROUGH TIME: the original, the versions that followed it, the one live
// editable copy, and eventually the final. `lineageKey` is what says two files are the same
// document at two moments rather than two different documents.
//
// THE CASE THAT DECIDED THE SHAPE, AND IT IS THE ONLY ONE THAT MATTERS TODAY:
// every document already on the platform was uploaded BEFORE marking existed. Not one of them
// carries a lineageKey, a code or a sequence. **An unmarked document is UNMARKED. It is not
// version zero, it is not the original, and it is not the live copy** - nobody decided any of those
// things about it. Reading absence as OV would be inventing a provenance the record does not have,
// which is the exact defect PD-4 exists to stop, and it would put a wrong mark on a page a Board
// reads. So unmarked documents are grouped alone and flagged `unmarked: true`, and the page says so.
//
// A LINEAGE WITH NO DV IS REPORTED, NOT REPAIRED. liveVersion() already returns null rather than
// picking the newest and hoping; groupLineages() carries that null through as `live: null` so the
// surface can say "no live copy" instead of nominating one.
// ---------------------------------------------------------------------------------------------

export function lineageKeyOf(d) {
  const k = d && d.lineageKey;
  return (typeof k === 'string' && k.trim()) ? k.trim() : null;
}

export function isMarked(d) {
  return !!lineageKeyOf(d) && !!(d && d.versionCode);
}

// Groups a claim's `documents` array into lineages, newest sequence last within each.
// Returns [{ key, unmarked, entries[], live, final, superseded[] }].
export function groupLineages(documents) {
  const list = Array.isArray(documents) ? documents : [];
  const byKey = new Map();
  const loose = [];

  list.forEach((d, i) => {
    if (!isMarked(d)) { loose.push({ ...d, __i: i }); return; }
    const k = lineageKeyOf(d);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push({ ...d, __i: i });
  });

  const out = [];
  byKey.forEach((entries, key) => {
    const sorted = entries.slice().sort((a, b) => {
      const as = Number(a.versionSequence), bs = Number(b.versionSequence);
      if (isFinite(as) && isFinite(bs) && as !== bs) return as - bs;
      return String(a.uploadedAt || '') < String(b.uploadedAt || '') ? -1 : 1;
    });
    out.push({
      key,
      unmarked: false,
      entries: sorted,
      live: liveVersion(sorted.map(e => ({ ...e, code: e.versionCode }))) ? sorted.find(e => e.versionCode === CODES.DV) : null,
      final: sorted.find(e => e.versionCode === CODES.FV) || null,
      superseded: sorted.filter(e => e.versionCode !== CODES.DV),
    });
  });

  // Each unmarked document stands alone and says so. Never folded into a lineage it was never part
  // of, and never given a code nobody assigned.
  loose.forEach(d => out.push({
    key: null, unmarked: true, entries: [d], live: null, final: null, superseded: [],
  }));

  return out;
}

// The sequence a NEW version of an existing lineage should take, read off the documents array.
export function nextSequenceInLineage(documents, key) {
  const k = String(key || '');
  const mine = (Array.isArray(documents) ? documents : [])
    .filter(d => lineageKeyOf(d) === k)
    .map(d => ({ sequence: Number(d.versionSequence), code: d.versionCode }));
  return nextSequence(mine);
}

// The `existing` array markFor() wants, built from a claim's documents for one lineage.
export function existingIn(documents, key) {
  const k = String(key || '');
  return (Array.isArray(documents) ? documents : [])
    .filter(d => lineageKeyOf(d) === k)
    .map(d => ({ sequence: Number(d.versionSequence), code: d.versionCode }));
}

// A new lineage key. Time plus randomness - it only has to be unique within one claim's documents.
export function newLineageKey() {
  return 'lin_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}
